/**
 * 템플릿 키를 템플릿 컴포넌트로 매핑
 * @param {string|number} templateKey - API에서 받은 템플릿 키 (11, 12, 13, 21-25, 31-33)
 * @returns {string} - 템플릿 컴포넌트 이름
 */
export const getTemplateComponent = (templateKey) => {
  const key = String(templateKey);
  const templateMap = {
    '11': 'Template11',
    '12': 'Template12',
    '13': 'Template13',
    '14': 'Template14',
    '21': 'Template21',
    '22': 'Template22',
    '23': 'Template23',
    '24': 'Template24',
    '25': 'Template25',
    '26': 'Template26',
    '31': 'Template31',
    '32': 'Template32',
    '33': 'Template33',
    '81': 'Template81',
  };

  return templateMap[key] || 'Template11'; // 기본값: Template11
};

/**
 * 템플릿 키로부터 템플릿 정보를 반환
 * @param {string|number} templateKey - 템플릿 키
 * @returns {{name: string, steps: number, surveys: number, type: string}}
 */
export const getTemplateInfo = (templateKey) => {
  const key = String(templateKey);
  const templates = {
    '11': { name: 'Template11', steps: 1, surveys: 0, type: 'single-step' },
    '12': { name: 'Template12', steps: 1, surveys: 1, type: 'single-step' },
    '13': { name: 'Template13', steps: 1, surveys: 2, type: 'single-step' },
    '21': { name: 'Template21', steps: 2, surveys: 1, type: 'two-step' },
    '22': { name: 'Template22', steps: 2, surveys: 1, type: 'two-step' },
    '23': { name: 'Template23', steps: 2, surveys: 0, type: 'two-step' },
    '24': { name: 'Template24', steps: 2, surveys: 2, type: 'two-step' },
    '25': { name: 'Template25', steps: 2, surveys: 2, type: 'two-step' },
    '26': { name: 'Template26', steps: 2, surveys: 1, type: 'two-step' },
    '31': { name: 'Template31', steps: 3, surveys: 1, type: 'three-step' },
    '32': { name: 'Template32', steps: 3, surveys: 2, type: 'three-step' },
    '33': { name: 'Template33', steps: 3, surveys: 2, type: 'three-step' }
  };

  return templates[key] || templates['11'];
};

/**
 * 이미지 URL 배열을 생성
 * @param {object} landingData - API에서 받은 랜딩 데이터
 * @param {number} count - 이미지 개수 (최대 6개)
 * @returns {Array<{url: string, alt: string}>}
 */
export const getImageArray = (landingData, count = 6) => {
  const images = [];
  for (let i = 1; i <= count; i++) {
    const url = landingData[`imgUrl${i}`];
    const alt = landingData[`imgALT${i}`] || `이미지 ${i}`;
    if (url) {
      images.push({ url, alt });
    }
  }
  return images;
};

/**
 * 스텝별로 이미지를 분리하여 반환
 * @param {object} landingData - API에서 받은 랜딩 데이터
 * @param {number} totalSteps - 전체 스텝 수 (2 or 3)
 * @returns {Object<string, Array<{url: string, alt: string}>>} - 스텝별 이미지 객체
 *
 * 예시:
 * 2-step: { step1: [img1, img2], step2: [img3, img4] }
 * 3-step: { step1: [img1, img2], step2: [img3, img4], step3: [img5, img6] }
 */
export const getImagesByStep = (landingData, totalSteps) => {
  const result = {};

  if (totalSteps === 2) {
    // 2-step: imgUrl1-2 → step1, imgUrl3-4 → step2
    result.step1 = [];
    result.step2 = [];

    for (let i = 1; i <= 4; i++) {
      const url = landingData[`imgUrl${i}`];
      const alt = landingData[`imgALT${i}`] || `이미지 ${i}`;
      if (url) {
        if (i <= 2) {
          result.step1.push({ url, alt });
        } else {
          result.step2.push({ url, alt });
        }
      }
    }
  } else if (totalSteps === 3) {
    // 3-step: imgUrl1-2 → step1, imgUrl3-4 → step2, imgUrl5-6 → step3
    result.step1 = [];
    result.step2 = [];
    result.step3 = [];

    for (let i = 1; i <= 6; i++) {
      const url = landingData[`imgUrl${i}`];
      const alt = landingData[`imgALT${i}`] || `이미지 ${i}`;
      if (url) {
        if (i <= 2) {
          result.step1.push({ url, alt });
        } else if (i <= 4) {
          result.step2.push({ url, alt });
        } else {
          result.step3.push({ url, alt });
        }
      }
    }
  }

  return result;
};

/**
 * 도메인 검증
 * @param {string} expectedDomain - API에서 받은 예상 도메인
 * @param {string} [currentDomain] - 현재 도메인 (선택사항, 기본값은 window.location.hostname)
 * @returns {boolean}
 */
export const validateDomain = (expectedDomain, currentDomain = null) => {
  const domain = currentDomain || window.location.hostname;
  if (domain === 'localhost' || domain === '127.0.0.1') return true;
  const domainWithoutSubdomain = domain.split('.')[0];
  return expectedDomain === domainWithoutSubdomain;
};

/**
 * 랜딩 상태 확인
 * @param {object} landingData - API에서 받은 랜딩 데이터
 * @returns {{isActive: boolean, message: string}}
 */
export const checkLandingStatus = (landingData) => {
  if (!landingData) {
    console.log('[Zentorga] checkLandingStatus: No landing data');
    return { isActive: false, message: '랜딩 데이터를 찾을 수 없습니다.' };
  }

  // landingStatus가 명시적으로 false인 경우만 차단 (undefined, null, true는 통과)
  if (landingData.landingStatus === false) {
    console.log('[Zentorga] checkLandingStatus: Landing status is false');
    return { isActive: false, message: '해당 페이지는 현재 이용할 수 없습니다.' };
  }

  // 도메인 검증 (domain 필드가 있을 때만 검증)
  if (landingData.domain) {
    const isValidDomain = validateDomain(landingData.domain);
    console.log('[Zentorga] Domain validation:', {
      expected: landingData.domain,
      current: window.location.hostname.split('.')[0],
      isValid: isValidDomain
    });

    if (!isValidDomain) {
      return { isActive: false, message: '잘못된 접근입니다. (도메인 불일치)' };
    }
  }

  console.log('[Zentorga] checkLandingStatus: All checks passed');
  return { isActive: true, message: '' };
};

export default {
  getTemplateComponent,
  getTemplateInfo,
  getImageArray,
  getImagesByStep,
  validateDomain,
  checkLandingStatus
};
